import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import inventoryApi from '../../../services/inventoryApi';
import SearchableInventorySupplierDropdown from '../../Common/SearchableInventorySupplierDropdown';
import LoadingSpinner from '../../Common/LoadingSpinner';

/**
 * Debit Note Form — create a debit note against a supplier from received (GRN) line items.
 */
const DebitNoteForm = ({ onClose, onSuccess }) => {
  const [supplier, setSupplier] = useState('');
  const [supplierLabel, setSupplierLabel] = useState('');
  const [availableGRNs, setAvailableGRNs] = useState([]);
  const [loadingGRNs, setLoadingGRNs] = useState(false);
  const [selected, setSelected] = useState({}); // key -> { grn, item, checked, quantity, unitPrice }
  const [taxAmount, setTaxAmount] = useState(0);
  const [reasonDescription, setReasonDescription] = useState('');
  const [notes, setNotes] = useState('');
  const [debitDate, setDebitDate] = useState(new Date().toISOString().split('T')[0]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = 'unset'; };
  }, []);

  useEffect(() => {
    if (supplier) loadGRNs(supplier);
    else { setAvailableGRNs([]); setSelected({}); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplier]);

  const loadGRNs = async (supplierId) => {
    setLoadingGRNs(true);
    try {
      const res = await inventoryApi.getAvailableGRNsForDebitNote(supplierId);
      setAvailableGRNs(res.data || []);
      setSelected({});
    } catch (err) {
      console.error('Error loading GRNs:', err);
      toast.error('Failed to load GRNs for this supplier');
      setAvailableGRNs([]);
    } finally {
      setLoadingGRNs(false);
    }
  };

  const itemKey = (grnId, itemId) => `${grnId}::${itemId}`;

  const toggleItem = (grn, item) => {
    const key = itemKey(grn._id, item._id);
    setSelected(prev => {
      const next = { ...prev };
      if (next[key]) {
        delete next[key];
      } else {
        next[key] = {
          grn,
          item,
          quantity: item.quantity || 1,
          unitPrice: item.unitPrice || 0
        };
      }
      return next;
    });
  };

  const updateSelected = (key, field, value) => {
    setSelected(prev => ({
      ...prev,
      [key]: { ...prev[key], [field]: parseFloat(value) || 0 }
    }));
  };

  const selectedList = Object.entries(selected).map(([key, v]) => ({ key, ...v }));
  const subtotal = selectedList.reduce((sum, s) => sum + (s.quantity * s.unitPrice), 0);
  const grandTotal = subtotal + (parseFloat(taxAmount) || 0);

  const handleSubmit = async () => {
    if (!supplier) { toast.error('Please select a supplier'); return; }
    if (selectedList.length === 0) { toast.error('Select at least one line item'); return; }

    // Build grns[] (unique) and lineItems[]
    const grnMap = new Map();
    const lineItems = selectedList.map(s => {
      const isFixedAsset = !!s.item.assetId;
      const totalValue = (s.quantity || 0) * (s.unitPrice || 0);
      if (!grnMap.has(s.grn._id)) {
        grnMap.set(s.grn._id, {
          grn: s.grn._id,
          grnNumber: s.grn.grnNumber,
          grnDate: s.grn.receiptDate,
          totalValue: 0
        });
      }
      grnMap.get(s.grn._id).totalValue += totalValue;

      return {
        ...(isFixedAsset ? { assetId: s.item.assetId } : { item: s.item.item?._id || s.item.item }),
        itemCode: s.item.itemCode,
        itemName: s.item.itemName,
        sourceGrn: s.grn._id,
        sourceGrnNumber: s.grn.grnNumber,
        grnQuantity: s.item.quantity,
        quantity: s.quantity,
        unitPrice: s.unitPrice,
        totalValue,
        unit: s.item.unit
      };
    });

    const payload = {
      supplier,
      grns: Array.from(grnMap.values()),
      lineItems,
      debitDate,
      reasonDescription,
      taxAmount: parseFloat(taxAmount) || 0,
      notes
    };

    setSaving(true);
    try {
      await inventoryApi.createDebitNote(payload);
      toast.success('Debit note created successfully');
      onSuccess?.();
    } catch (err) {
      console.error('Error creating debit note:', err);
      toast.error(err.response?.data?.message || 'Failed to create debit note');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 backdrop-blur-sm bg-black/40 overflow-y-auto z-50 flex items-center justify-center p-4">
      <div className="relative w-full max-w-5xl max-h-[92vh] bg-white shadow-2xl rounded-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex-shrink-0 bg-gradient-to-r from-amber-600 to-orange-600 px-6 py-3 flex items-center justify-between">
          <h3 className="text-xl font-bold text-white">New Debit Note</h3>
          <button onClick={onClose} className="w-9 h-9 rounded-lg flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Supplier + Date */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Supplier <span className="text-red-500">*</span></label>
              <SearchableInventorySupplierDropdown
                value={supplier}
                displayLabel={supplierLabel}
                onChange={(val, label) => { setSupplier(val); setSupplierLabel(label || ''); }}
                placeholder="Select supplier"
                menuPlacement="down"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Debit Date</label>
              <input
                type="date"
                value={debitDate}
                onChange={(e) => setDebitDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
            </div>
          </div>

          {/* GRN line items */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-700">Select GRN Line Items</div>
            <div className="p-3">
              {!supplier ? (
                <p className="text-sm text-gray-500 py-4 text-center">Select a supplier to load received items</p>
              ) : loadingGRNs ? (
                <div className="flex items-center justify-center py-6"><LoadingSpinner /><span className="ml-2 text-gray-600">Loading GRNs...</span></div>
              ) : availableGRNs.length === 0 ? (
                <p className="text-sm text-gray-500 py-4 text-center">No GRNs found for this supplier</p>
              ) : (
                <div className="space-y-4">
                  {availableGRNs.map(grn => (
                    <div key={grn._id} className="border border-gray-200 rounded">
                      <div className="bg-gray-50 px-3 py-1.5 text-xs font-semibold text-gray-700 flex justify-between">
                        <span>{grn.grnNumber} · {grn.receiptDate ? new Date(grn.receiptDate).toLocaleDateString() : ''}</span>
                        <span>{grn.procurementType}</span>
                      </div>
                      <table className="w-full text-sm">
                        <thead className="bg-white text-xs text-gray-500">
                          <tr>
                            <th className="px-2 py-1 text-left w-8"></th>
                            <th className="px-2 py-1 text-left">Item</th>
                            <th className="px-2 py-1 text-right">GRN Qty</th>
                            <th className="px-2 py-1 text-right">Qty</th>
                            <th className="px-2 py-1 text-right">Unit Price</th>
                            <th className="px-2 py-1 text-right">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(grn.items || []).map(item => {
                            const key = itemKey(grn._id, item._id);
                            const sel = selected[key];
                            return (
                              <tr key={key} className="border-t border-gray-100">
                                <td className="px-2 py-1">
                                  <input type="checkbox" checked={!!sel} onChange={() => toggleItem(grn, item)} />
                                </td>
                                <td className="px-2 py-1">
                                  <div className="font-medium text-gray-800">{item.itemName}</div>
                                  <div className="text-xs text-gray-500">{item.itemCode}</div>
                                </td>
                                <td className="px-2 py-1 text-right text-gray-600">{item.quantity}</td>
                                <td className="px-2 py-1 text-right">
                                  {sel ? (
                                    <input type="number" min="0.01" step="0.01" value={sel.quantity}
                                      onChange={(e) => updateSelected(key, 'quantity', e.target.value)}
                                      className="w-20 px-1 py-0.5 border border-gray-300 rounded text-right" />
                                  ) : '-'}
                                </td>
                                <td className="px-2 py-1 text-right">
                                  {sel ? (
                                    <input type="number" min="0" step="0.01" value={sel.unitPrice}
                                      onChange={(e) => updateSelected(key, 'unitPrice', e.target.value)}
                                      className="w-24 px-1 py-0.5 border border-gray-300 rounded text-right" />
                                  ) : item.unitPrice?.toFixed?.(2)}
                                </td>
                                <td className="px-2 py-1 text-right font-medium">
                                  {sel ? (sel.quantity * sel.unitPrice).toFixed(2) : '-'}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Reason / notes / totals */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Reason / Description</label>
              <textarea rows="3" value={reasonDescription} onChange={(e) => setReasonDescription(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 resize-none text-sm"
                placeholder="Reason for the debit note (e.g. price difference, short delivery)..." />
              <label className="block text-sm font-semibold text-gray-700 mb-1 mt-3">Notes</label>
              <textarea rows="2" value={notes} onChange={(e) => setNotes(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 resize-none text-sm" />
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-2 h-fit">
              <div className="flex justify-between text-sm"><span className="text-gray-600">Subtotal</span><span className="font-medium">{subtotal.toFixed(2)}</span></div>
              <div className="flex justify-between text-sm items-center">
                <span className="text-gray-600">Tax Amount</span>
                <input type="number" min="0" step="0.01" value={taxAmount} onChange={(e) => setTaxAmount(e.target.value)}
                  className="w-24 px-2 py-1 border border-gray-300 rounded text-right" />
              </div>
              <div className="flex justify-between text-base font-bold border-t border-gray-300 pt-2">
                <span>Total</span><span className="text-amber-700">{grandTotal.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 bg-gray-50 px-5 py-3 border-t border-gray-200 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-1.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">Cancel</button>
          <button onClick={handleSubmit} disabled={saving}
            className="px-4 py-1.5 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white text-sm font-medium rounded-lg disabled:opacity-50">
            {saving ? 'Creating...' : 'Create Debit Note'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DebitNoteForm;
